/**
 * useYahooOAuth — manages the Yahoo OAuth 2.0 connect flow.
 *
 * Does NOT use safeFetch because Yahoo endpoints can return 401 for
 * "no credentials / token expired" — distinct from the app-level 401
 * that means the user's DRF token is invalid. Using raw fetch here lets
 * us handle the two cases separately.
 */

import { useState, useEffect, useRef, useCallback } from 'react';

const POLL_INTERVAL_MS  = 2000;
const POPUP_CHECK_MS    = 500;
const POLL_TIMEOUT_MS   = 5 * 60 * 1000; // 5 minutes

function authHeaders() {
  const token = localStorage.getItem('doritostatsToken');
  return token ? { Authorization: `Token ${token}` } : {};
}

export default function useYahooOAuth(year, { onSuccess } = {}) {
  // 'idle' | 'checking' | 'connected' | 'disconnected'
  const [status, setStatus] = useState('idle');
  const [leagues, setLeagues] = useState(null);
  const [leaguesLoading, setLeaguesLoading] = useState(false);
  const [leaguesError, setLeaguesError] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState('');

  const pollingRef    = useRef(null);
  const popupCheckRef = useRef(null);
  const timeoutRef    = useRef(null);
  const mountedRef    = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const stopAll = useCallback(() => {
    clearInterval(pollingRef.current);
    clearInterval(popupCheckRef.current);
    clearTimeout(timeoutRef.current);
    pollingRef.current = null;
    popupCheckRef.current = null;
    timeoutRef.current = null;
  }, []);

  useEffect(() => () => stopAll(), [stopAll]);

  // ------------------------------------------------------------------
  // Status check
  // ------------------------------------------------------------------
  const checkStatus = useCallback(async () => {
    const resp = await fetch('/api/auth/yahoo/status/', { headers: authHeaders() });
    if (!resp.ok) return false;
    const data = await resp.json();
    return data.connected === true;
  }, []);

  // ------------------------------------------------------------------
  // League fetch — does NOT use safeFetch to avoid false logout on 401
  // ------------------------------------------------------------------
  const fetchLeagues = useCallback(async (yr) => {
    if (!mountedRef.current) return;
    setLeaguesLoading(true);
    setLeaguesError('');
    try {
      const resp = await fetch(`/api/auth/yahoo/leagues/?year=${yr}`, { headers: authHeaders() });
      const data = await resp.json();
      if (!mountedRef.current) return;
      if (resp.ok) {
        setLeagues(data.leagues || []);
      } else if (data.error === 'no_credentials' || data.error === 'token_expired') {
        setStatus('disconnected');
        setLeagues(null);
        if (data.error === 'token_expired') {
          setLeaguesError('Your Yahoo session has expired. Please reconnect.');
        }
      } else if (data.error === 'fantasy_api_not_authorized') {
        setLeagues(null);
        setLeaguesError(
          'Your Yahoo account is connected, but Doritostats is still awaiting Yahoo Fantasy API access. ' +
          'Yahoo review typically takes 1–2 weeks. Your leagues will appear once Yahoo approves the request.'
        );
      } else if (data.error === 'yahoo_league_discovery_failed') {
        setLeagues(null);
        setLeaguesError('Yahoo could not load your leagues right now. Please try again shortly.');
      } else {
        setLeagues(null);
        setLeaguesError(data.error || 'Failed to load Yahoo leagues.');
      }
    } catch {
      if (mountedRef.current) setLeaguesError('Failed to load Yahoo leagues. Check your connection.');
    } finally {
      if (mountedRef.current) setLeaguesLoading(false);
    }
  }, []);

  // ------------------------------------------------------------------
  // OAuth completion / error handlers
  // ------------------------------------------------------------------
  const onConnected = useCallback((yr) => {
    stopAll();
    if (!mountedRef.current) return;
    setConnecting(false);
    setStatus('connected');
    fetchLeagues(yr);
    onSuccess?.();
  }, [stopAll, fetchLeagues, onSuccess]);

  const onConnectFailed = useCallback((reason) => {
    stopAll();
    if (!mountedRef.current) return;
    setConnecting(false);
    const msg =
      reason === 'invalid_state'           ? 'Authorization failed (invalid state). Please try again.' :
      reason === 'token_exchange_failed'    ? 'Yahoo token exchange failed. Please try again.' :
      reason === 'provider_error'           ? 'Yahoo declined the authorization. Please try again.' :
      reason === 'popup_closed'             ? '' : // silent — user cancelled
      'Yahoo connection failed. Please try again.';
    setConnectError(msg);
  }, [stopAll]);

  // ------------------------------------------------------------------
  // postMessage from popup (fires when callback page closes itself)
  // ------------------------------------------------------------------
  useEffect(() => {
    const handler = (event) => {
      // Allow messages from any localhost origin (http/https may differ in local dev)
      if (!event.origin.includes('localhost') && event.origin !== window.location.origin) return;
      if (event.data?.type === 'yahoo_oauth_success') onConnected(year);
      if (event.data?.type === 'yahoo_oauth_error')   onConnectFailed(event.data.reason);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onConnected, onConnectFailed, year]);

  // ------------------------------------------------------------------
  // Initialize — check status and optionally load leagues
  // ------------------------------------------------------------------
  const initialize = useCallback(async (yr) => {
    if (!mountedRef.current) return;
    setStatus('checking');
    setLeagues(null);
    setLeaguesError('');
    setConnectError('');
    try {
      const connected = await checkStatus();
      if (!mountedRef.current) return;
      setStatus(connected ? 'connected' : 'disconnected');
      if (connected) fetchLeagues(yr);
    } catch {
      if (mountedRef.current) setStatus('disconnected');
    }
  }, [checkStatus, fetchLeagues]);

  // ------------------------------------------------------------------
  // Disconnect — clears tokens server-side and purges Yahoo league data
  // ------------------------------------------------------------------
  const [disconnecting, setDisconnecting] = useState(false);
  const disconnect = useCallback(async () => {
    setDisconnecting(true);
    setConnectError('');
    try {
      const resp = await fetch('/api/auth/yahoo/disconnect/', {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.error || 'Failed to disconnect Yahoo.');
      if (!mountedRef.current) return data;
      setStatus('disconnected');
      setLeagues(null);
      setLeaguesError('');
      return data;
    } catch (err) {
      if (mountedRef.current) setConnectError(err.message || 'Failed to disconnect Yahoo.');
      return null;
    } finally {
      if (mountedRef.current) setDisconnecting(false);
    }
  }, []);

  // ------------------------------------------------------------------
  // Start OAuth popup
  // ------------------------------------------------------------------
  const startOAuth = useCallback(async (yr) => {
    setConnectError('');
    try {
      const resp = await fetch('/api/auth/yahoo/start/', { headers: authHeaders() });
      if (!resp.ok) throw new Error('start failed');
      const { auth_url } = await resp.json();

      const popup = window.open(
        auth_url, 'yahoo_oauth',
        'width=620,height=720,left=300,top=100,noopener=0'
      );
      if (!popup) {
        setConnectError('Popup blocked. Please allow popups for this site and try again.');
        return;
      }

      // Clear timers from any previous attempt before creating new ones.
      // Without this, a second call overwrites the interval refs and the
      // first popup-check interval leaks, firing status + leagues requests
      // every 500ms until the page reloads.
      stopAll();
      setConnecting(true);

      // Detect user closing the popup without completing auth.
      // Do a final status check first — the popup may have finished the token
      // exchange just before closing (postMessage could have fired cross-origin).
      popupCheckRef.current = setInterval(async () => {
        if (popup.closed) {
          stopAll();
          if (!mountedRef.current) return;
          try {
            const connected = await checkStatus();
            if (connected) onConnected(yr);
            else setConnecting(false);
          } catch {
            setConnecting(false);
          }
        }
      }, POPUP_CHECK_MS);

      // Poll status in case postMessage doesn't fire
      pollingRef.current = setInterval(async () => {
        try {
          const connected = await checkStatus();
          if (connected) onConnected(yr);
        } catch { /* ignore transient errors */ }
      }, POLL_INTERVAL_MS);

      // Hard timeout
      timeoutRef.current = setTimeout(() => {
        if (!mountedRef.current) return;
        setConnecting(false);
        setConnectError('Authorization timed out. Please try again.');
        stopAll();
      }, POLL_TIMEOUT_MS);

    } catch {
      if (mountedRef.current) setConnectError('Failed to start Yahoo authorization. Please try again.');
    }
  }, [checkStatus, onConnected, stopAll]);

  return {
    status,          // 'idle' | 'checking' | 'connected' | 'disconnected'
    leagues,         // null | Array<{league_id, name, num_teams}>
    leaguesLoading,
    leaguesError,
    connecting,      // OAuth popup is open, waiting
    connectError,
    initialize,
    startOAuth,
    disconnect,
    disconnecting,
    reloadLeagues: fetchLeagues,
  };
}
